import { useMemo } from 'react';

import { TableColumn } from '@app/components';

import { Typography } from '@mui/material';
import { QrCodeRiskLogOutDto } from '@app/swagger-types';
import { NestedKeyOf } from '@app/utils/type.utils';
import { format } from 'date-fns';
import { FORMAT } from '@app/constants/formats';
import { clsxm } from '@app/styles/clsxm';

const getEntityField = (v: NestedKeyOf<QrCodeRiskLogOutDto>) => v;

export const useTableColumns = () => {
  const tableColumns = useMemo<TableColumn<QrCodeRiskLogOutDto>[]>(() => {
    return [
      {
        headerName: 'Created date',
        field: getEntityField('createdDate'),
        align: 'center',
        firstClickSort: 'desc',
        render: ({ createdDate }) => {
          return (
            <Typography variant="xs">{createdDate ? format(new Date(createdDate), FORMAT.FULL_TIME) : '-'}</Typography>
          );
        },
      },
      {
        headerName: 'QR code',
        field: getEntityField('qrCodeId'),
        align: 'left',
        disableSort: true,
        render: ({ qrCodeId }) => (
          <Typography variant="xs" noWrap>
            {qrCodeId ?? '-'}
          </Typography>
        ),
      },
      {
        headerName: 'Source',
        field: getEntityField('sourceAction'),
        align: 'left',
        disableSort: true,
        render: ({ sourceAction, accountCategory, clientIp }) => (
          <div className="flex flex-col items-start gap-1">
            <Typography variant="xs">{sourceAction}</Typography>
            <span className="text-xxs text-gray-500">
              {accountCategory}
              {clientIp ? ` · ${clientIp}` : ''}
            </span>
          </div>
        ),
      },
      {
        headerName: 'Input URL',
        field: getEntityField('inputUrl'),
        align: 'left',
        disableSort: true,
        render: ({ inputUrl, finalUrl }) => (
          <div className="flex flex-col items-start gap-1 max-w-[320px]">
            <Typography variant="xs" className="truncate w-full">
              {inputUrl}
            </Typography>
            {finalUrl && finalUrl !== inputUrl ? (
              <span className="text-xxs text-gray-500 truncate w-full">→ {finalUrl}</span>
            ) : null}
          </div>
        ),
      },
      {
        headerName: 'Local check',
        field: getEntityField('localCheckVerdict'),
        align: 'left',
        disableSort: true,
        render: ({ localCheckVerdict }) => <Typography variant="xs">{localCheckVerdict}</Typography>,
      },
      {
        headerName: 'Resolution',
        field: getEntityField('resolutionVerdict'),
        align: 'left',
        disableSort: true,
        render: ({ resolutionVerdict }) => <Typography variant="xs">{resolutionVerdict ?? '-'}</Typography>,
      },
      {
        headerName: 'Web Risk',
        field: getEntityField('webRiskVerdict'),
        align: 'left',
        disableSort: true,
        render: ({ webRiskVerdict }) => <Typography variant="xs">{webRiskVerdict ?? '-'}</Typography>,
      },
      {
        headerName: 'Rate limit',
        field: getEntityField('rateLimitExceeded'),
        align: 'center',
        disableSort: true,
        render: ({ rateLimitExceeded }) =>
          rateLimitExceeded ? <span className="text-xs rounded px-1 text-white bg-amber-600">exceeded</span> : null,
      },
      {
        headerName: 'Would block',
        field: getEntityField('wouldBlock'),
        align: 'center',
        firstClickSort: 'desc',
        render: ({ wouldBlock }) => (
          <span className={clsxm('text-xs rounded px-1 text-white', wouldBlock ? 'bg-red-600' : 'bg-emerald-600')}>
            {wouldBlock ? 'would block' : 'clean'}
          </span>
        ),
      },
      {
        headerName: 'Status',
        field: getEntityField('reviewed'),
        align: 'center',
        disableSort: true,
        render: ({ reviewed }) => (
          <span className={clsxm('text-xs rounded px-1 text-white', reviewed ? 'bg-slate-500' : 'bg-amber-600')}>
            {reviewed ? 'reviewed' : 'open'}
          </span>
        ),
      },
    ];
  }, []);

  return tableColumns;
};
